import { ArrowUp } from 'lucide-react';

/**
 * Pill input plus a 44px circular send button.
 *
 * This is the only ember-filled control in the entire product, and it uses
 * `--color-accent-announcement-text` for 5.69:1 against white (spec §6). Ember never fills any
 * other button, which is why this lives here rather than as a Button variant that could be reused
 * by accident.
 *
 * It sat inside `EventDetailScreen` and was imported from there by both thread screens, which was
 * fine while that file owned the only inline thread. TROOP-116 gave the thread its own component
 * and left the event screen a thin shell, so importing the composer from a page that no longer
 * renders one would have been a stale breadcrumb.
 */
export function Composer({ onSend }: { onSend?: (body: string) => void | Promise<void> }) {
  return (
    <form
      className="flex items-center gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!onSend) return;

        const input = e.currentTarget.elements.namedItem('composer') as HTMLInputElement | null;
        const body = input?.value.trim();
        if (!body) return;

        // Cleared before awaiting so the field feels immediate; a failure surfaces
        // as an error banner rather than by restoring text the sender believes went.
        if (input) input.value = '';
        void onSend(body);
      }}
      style={{
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-full)',
        padding: 'var(--space-1) var(--space-1) var(--space-1) var(--space-4)',
      }}
    >
      <label className="sr-only" htmlFor="composer">
        Message this thread
      </label>
      <input
        id="composer"
        name="composer"
        placeholder="Message the thread…"
        autoComplete="off"
        className="t-body flex-1 min-w-0"
        style={{
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--color-text-primary)',
          minHeight: 'var(--tap-min)',
        }}
      />
      <button
        type="submit"
        aria-label="Send message"
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 'var(--tap-min)',
          height: 'var(--tap-min)',
          borderRadius: 'var(--radius-full)',
          background: 'var(--color-accent-announcement-text)',
          // SPEC-GAP: §6 specifies this contrast "against white" (5.69:1) but the
          // palette has no white token. --color-surface-raised is #FFFFFF in light
          // mode but becomes #18212D in dark, which would fail on the ember fill.
          // Literal white is the specified value; a new token would be improvising.
          color: '#FFFFFF',
          border: 'none',
        }}
      >
        <ArrowUp size={20} aria-hidden="true" />
      </button>
    </form>
  );
}
